// Pure date and copy helpers for the folder expiration feature (Phase 4).
// No import declarations on purpose: Node loads this file directly in the phase checks.
// Day arithmetic works on year/month/day parts through UTC, never through a local-time
// parse of the string: parsing an ISO datetime in local time shifts the calendar day.

export const URGENT_DAYS = 3
export const BADGE_PREFIX = 'Disponibile fino al'
export const INVALID_DATE_TEXT = 'Data di scadenza non valida: scegli una data da domani in poi.'
export const MODAL_TITLE = 'Scadenza cartella'
export const MODAL_HINT_TEXT = 'La cartella sarà automaticamente spostata nel cestino alle 00:05 del giorno di scadenza e sarà recuperabile per 5 anni.'
export const SAVE_SUCCESS_TEXT = 'Scadenza impostata.'
export const SET_ERROR_OVERRIDES = {
  422: INVALID_DATE_TEXT,
  403: 'Non hai il permesso di impostare la scadenza.',
  404: 'La cartella non esiste più.',
}
export const REMOVE_SUCCESS_TEXT = 'Scadenza rimossa.'
export const REMOVE_CONFIRM = {
  title: 'Rimuovi scadenza?',
  body: 'La cartella non avrà più una data di scadenza.',
  okTitle: 'OK',
  cancelTitle: 'Annulla',
}
// The DELETE 422 is not described by the backend PRD (A9): the text is neutral on purpose.
export const REMOVE_ERROR_OVERRIDES = {
  422: 'Non è possibile rimuovere la scadenza di questo elemento.',
  403: 'Non hai il permesso di rimuovere la scadenza.',
  404: 'La cartella non esiste più.',
}

const DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]|$)/
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/
const MS_PER_DAY = 86400000

const pad = (value, length) => String(value).padStart(length, '0')

const toYmd = ({ year, month, day }) => `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`

// Date.UTC treats years 0-99 as 1900-1999, so the parts go through setUTCFullYear instead.
const utcDate = (year, month, day) => {
  const date = new Date(0)
  date.setUTCFullYear(year, month - 1, day)
  return date
}

const partsOf = date => ({
  year: date.getUTCFullYear(),
  month: date.getUTCMonth() + 1,
  day: date.getUTCDate(),
})

const addDays = (parts, delta) => partsOf(utcDate(parts.year, parts.month, parts.day + delta))

const dayNumber = parts => Math.round(utcDate(parts.year, parts.month, parts.day).getTime() / MS_PER_DAY)

const localParts = now => ({ year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() })

// Leading YYYY-MM-DD of a date or datetime string as { year, month, day }, or null.
export const parseDateOnly = value => {
  if (typeof value !== 'string') {
    return null
  }
  const match = DATE_PREFIX.exec(value)
  if (!match) {
    return null
  }
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const check = partsOf(utcDate(year, month, day))
  if (check.year !== year || check.month !== month || check.day !== day) {
    return null
  }
  return { year, month, day }
}

export const normalizeDate = value => {
  const parts = parseDateOnly(value)
  return parts ? toYmd(parts) : null
}

// D-12: "tomorrow" is computed from the LOCAL calendar day, one UTC-arithmetic day later.
export const getTomorrowDateString = (now = new Date()) => toYmd(addDays(localParts(now), 1))

// D-05: the folder goes to the trash at 00:05 on expiration_date, so it is available until the day before.
export const getAvailableUntil = expirationDate => {
  const parts = parseDateOnly(expirationDate)
  return parts ? toYmd(addDays(parts, -1)) : null
}

const defaultFormat = ymd => `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}/${ymd.slice(0, 4)}`

// D-12: display goes through the optional formatter (the badge passes dateHelpers.format).
export const expirationDateDisplay = (expirationDate, formatYmd) => {
  const availableUntil = getAvailableUntil(expirationDate)
  if (availableUntil === null) {
    return null
  }
  return typeof formatYmd === 'function' ? formatYmd(availableUntil) : defaultFormat(availableUntil)
}

export const getDaysUntilExpiration = (expirationDate, now = new Date()) => {
  const parts = parseDateOnly(expirationDate)
  if (!parts) {
    return null
  }
  return dayNumber(parts) - dayNumber(localParts(now))
}

export const isExpirationUrgent = (expirationDate, now = new Date()) => {
  const days = getDaysUntilExpiration(expirationDate, now)
  return days !== null && days <= URGENT_DAYS
}

export const getExpirationVariant = (expirationDate, now = new Date()) => {
  if (getDaysUntilExpiration(expirationDate, now) === null) {
    return null
  }
  return isExpirationUrgent(expirationDate, now) ? 'danger' : 'warning'
}

export const isValidNewExpiration = (value, now = new Date()) => {
  if (typeof value !== 'string' || !DATE_ONLY.test(value) || parseDateOnly(value) === null) {
    return false
  }
  return value >= getTomorrowDateString(now)
}

const isPlainObject = value => typeof value === 'object' && value !== null && !Array.isArray(value)

// A2: the reply date wins when it parses, the value the user chose is the fallback.
// sentDate null means a removal: the confirmed result is a null date.
export const readExpirationUpdate = (response, id, sentDate) => {
  try {
    if (!(response && response.data && Number(response.data.code) === 1)) {
      return null
    }
    const record = response.data.data
    if (!isPlainObject(record)) {
      return null
    }
    if (id === null || id === undefined) {
      return null
    }
    if (sentDate === null) {
      return { id, expiration_date: null }
    }
    if (typeof sentDate !== 'string') {
      return null
    }
    const confirmed = normalizeDate(record.expiration_date)
    return { id, expiration_date: confirmed !== null ? confirmed : sentDate }
  } catch (error) {
    return null
  }
}

// What the modal does after a failed request: keep an inline message, close, refresh the list.
export const getFailureOutcome = (status, hasFieldError) => {
  const outcome = { inline: false, close: false, stale: false }
  if (status === 422 || hasFieldError === true) {
    outcome.inline = true
  }
  if (status === 403) {
    outcome.close = true
  }
  if (status === 404) {
    outcome.close = true
    outcome.stale = true
  }
  return outcome
}

export default {
  URGENT_DAYS,
  BADGE_PREFIX,
  INVALID_DATE_TEXT,
  MODAL_TITLE,
  MODAL_HINT_TEXT,
  SAVE_SUCCESS_TEXT,
  SET_ERROR_OVERRIDES,
  REMOVE_SUCCESS_TEXT,
  REMOVE_CONFIRM,
  REMOVE_ERROR_OVERRIDES,
  parseDateOnly,
  normalizeDate,
  getTomorrowDateString,
  getAvailableUntil,
  expirationDateDisplay,
  getDaysUntilExpiration,
  isExpirationUrgent,
  getExpirationVariant,
  isValidNewExpiration,
  readExpirationUpdate,
  getFailureOutcome,
}
